/**
 * SEO › Sitemap (`/seo/sitemap`) — every URL the site's sitemap
 * declares, HTTP-checked server-side for breakage / redirect chains /
 * noindex (plans/seo quick slice 2).
 *
 * Promoted out of the `/seo` landing page when the section grew its
 * sidebar panel — same move Performance made with its `?view=` tabs.
 * The check itself is a real sitemap-wide fan-out on the API side,
 * KV-cached for 6h per site; Re-check forces a fresh pass. The
 * crawler plan (plans/seo/crawler) supersedes this with stored,
 * trended findings.
 */
import { EmptyState } from '@/components/primitives/EmptyState';
import { SectionBoundary } from '@/components/primitives/SectionBoundary';
import { SiteIsland } from '@/components/shell/SiteIsland';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useActiveSiteUuid } from '@/lib/active-site';
import { formatCount, relativeTime } from '@/lib/format';
import {
  type SitemapHealth,
  type SitemapHealthUrl,
  useRefreshSitemapHealth,
  useSitemapHealth,
} from '@/lib/queries';
import { useSiteTopPages } from '@/lib/queries/pixel';
import { ArrowsClockwise, CheckCircle, TreeStructure, Warning } from '@phosphor-icons/react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { type ReactElement, useMemo } from 'react';

const ISSUE_FILTERS = ['broken', 'redirect', 'noindexed', 'canonical', 'titles'] as const;

/** Which slice of the issues table a tile / bar segment narrows to.
 *  Categories overlap (a URL can be noindexed AND missing a title),
 *  so these are predicates over rows, not partitions. */
type IssueFilter = (typeof ISSUE_FILTERS)[number];

interface SitemapSearch {
  /** Active issue filter — in the URL so a filtered view is shareable. */
  filter?: IssueFilter;
}

export const Route = createFileRoute('/_app/seo/sitemap')({
  validateSearch: (search: Record<string, unknown>): SitemapSearch =>
    ISSUE_FILTERS.includes(search.filter as IssueFilter)
      ? { filter: search.filter as IssueFilter }
      : {},
  component: SitemapPage,
});

function SitemapPage(): ReactElement {
  const siteUuid = useActiveSiteUuid();
  return (
    <SiteIsland section="Sitemap" parent={{ label: 'SEO', href: '/seo' }}>
      {siteUuid && (
        <SectionBoundary label="sitemap health">
          <SitemapHealthCard siteUuid={siteUuid} />
        </SectionBoundary>
      )}
    </SiteIsland>
  );
}

function issueLabel(row: SitemapHealthUrl, origin: string): string {
  if (row.error) return row.error;
  if (row.status != null && row.status >= 400) return `HTTP ${row.status}`;
  const parts: string[] = [];
  if (row.noindex) parts.push('noindex');
  if (row.canonical_mismatch && row.canonical)
    parts.push(`canonical → ${displayUrl(row.canonical, origin)}`);
  if (row.duplicate_title) parts.push('duplicate title');
  if (row.missing_title) parts.push('missing title');
  if (row.missing_description) parts.push('missing meta description');
  if (row.redirect_hops > 0) {
    const dest = row.final_url !== row.url ? ` → ${displayUrl(row.final_url, origin)}` : '';
    parts.push(`${row.redirect_hops} redirect ${row.redirect_hops === 1 ? 'hop' : 'hops'}${dest}`);
  }
  return parts.join(' · ') || '—';
}

function pathOf(url: string): string {
  try {
    const u = new URL(url);
    return `${u.pathname}${u.search}` || '/';
  } catch {
    return url;
  }
}

/** Path for display, keeping the host visible when it isn't the
 *  site's own — hreflang sitemaps list locale-subdomain URLs, and 15
 *  identical-looking `/pages/x` rows are unreadable without it. */
function displayUrl(url: string, origin: string): string {
  try {
    const u = new URL(url);
    const path = `${u.pathname}${u.search}` || '/';
    return u.hostname === new URL(origin).hostname ? path : `${u.hostname}${path}`;
  } catch {
    return url;
  }
}

const FILTER_PREDICATES: Record<IssueFilter, (row: SitemapHealthUrl) => boolean> = {
  broken: (row) => row.status === null || (row.status ?? 0) >= 400 || row.error !== null,
  redirect: (row) => row.redirect_hops > 0,
  noindexed: (row) => row.noindex,
  canonical: (row) => row.canonical_mismatch,
  titles: (row) => row.missing_title || row.duplicate_title || row.missing_description,
};

const FILTER_LABELS: Record<IssueFilter, string> = {
  broken: 'broken URLs',
  redirect: 'redirects',
  noindexed: 'noindexed pages',
  canonical: 'canonical mismatches',
  titles: 'title & meta issues',
};

interface FilterProps {
  active: IssueFilter | null;
  onToggle: (filter: IssueFilter) => void;
}

/** Severity order, mirroring the API's worst-first sort — needed
 *  client-side so the traffic re-sort stays severity-stable. */
function severityRank(row: SitemapHealthUrl): number {
  if (row.status === null || (row.status ?? 0) >= 400 || row.error !== null) return 0;
  if (row.noindex) return 1;
  if (row.canonical_mismatch) return 2;
  if (row.duplicate_title || row.missing_title) return 3;
  if (row.missing_description) return 4;
  return 5;
}

/** Trailing-slash-insensitive pathname key for joining issue URLs
 *  with pixel top-pages rows. */
function pathKey(url: string): string {
  try {
    return new URL(url).pathname.replace(/\/+$/, '').toLowerCase() || '/';
  } catch {
    return url.toLowerCase();
  }
}

/** Part-to-whole strip of the three exclusive per-URL states. The
 *  finding counts (noindexed / canonical / titles) overlap the same
 *  URLs, so they stay as tiles — stacking them would double-count.
 *  Problem segments filter the issues table below; OK has no rows
 *  to show, so it stays inert. */
function DistributionBar({
  health,
  active,
  onToggle,
}: { health: SitemapHealth } & FilterProps): ReactElement | null {
  const allSegments: Array<{
    label: string;
    value: number;
    color: string;
    filter: IssueFilter | null;
  }> = [
    { label: 'OK', value: health.summary.ok, color: 'var(--color-success)', filter: null },
    {
      label: 'Redirects',
      value: health.summary.redirect,
      color: 'var(--color-warning)',
      filter: 'redirect',
    },
    {
      label: 'Broken',
      value: health.summary.broken,
      color: 'var(--color-danger)',
      filter: 'broken',
    },
  ];
  const segments = allSegments.filter((s) => s.value > 0);
  if (segments.length === 0) return null;
  return (
    <div className="px-4 py-3">
      <div className="flex h-2 gap-0.5 overflow-hidden rounded-full">
        {segments.map((s) =>
          s.filter ? (
            <button
              key={s.label}
              type="button"
              aria-pressed={active === s.filter}
              onClick={() => onToggle(s.filter as IssueFilter)}
              className={`min-w-1 cursor-pointer rounded-[1px] first:rounded-l-full last:rounded-r-full ${
                active && active !== s.filter ? 'opacity-40' : ''
              }`}
              style={{ flexGrow: s.value, backgroundColor: s.color }}
              title={`${s.label}: ${s.value} of ${health.checked} — click to filter the list`}
            />
          ) : (
            <div
              key={s.label}
              className={`min-w-1 rounded-[1px] first:rounded-l-full last:rounded-r-full ${
                active ? 'opacity-40' : ''
              }`}
              style={{ flexGrow: s.value, backgroundColor: s.color }}
              title={`${s.label}: ${s.value} of ${health.checked}`}
            />
          ),
        )}
      </div>
    </div>
  );
}

function TileBody({
  tile,
}: { tile: { label: string; value: number; hint: string; alert?: boolean } }): ReactElement {
  return (
    <>
      <p className="text-xs font-medium text-[var(--color-mute)]" title={tile.hint}>
        {tile.label}
      </p>
      <p
        className={`mt-0.5 text-xl font-semibold tabular-nums ${
          tile.alert ? 'text-[var(--color-danger,#dc2626)]' : 'text-[var(--color-ink)]'
        }`}
      >
        {tile.value}
      </p>
    </>
  );
}

function SummaryTiles({
  health,
  active,
  onToggle,
}: { health: SitemapHealth } & FilterProps): ReactElement {
  const tiles: Array<{
    label: string;
    value: number;
    hint: string;
    alert?: boolean;
    filter: IssueFilter | null;
  }> = [
    { label: 'OK', value: health.summary.ok, hint: 'Direct 2xx responses', filter: null },
    {
      label: 'Redirects',
      value: health.summary.redirect,
      hint: 'Sitemap should list final URLs',
      filter: 'redirect',
    },
    {
      label: 'Broken',
      value: health.summary.broken,
      hint: '4xx / 5xx / unreachable',
      alert: health.summary.broken > 0,
      filter: 'broken',
    },
    {
      label: 'Noindexed',
      value: health.summary.noindexed,
      hint: 'In the sitemap but told not to index',
      alert: health.summary.noindexed > 0,
      filter: 'noindexed',
    },
    {
      label: 'Canonical',
      value: health.summary.canonical_mismatch,
      hint: 'Page canonicalizes to a different URL — search engines ignore its sitemap entry',
      alert: health.summary.canonical_mismatch > 0,
      filter: 'canonical',
    },
    {
      label: 'Titles & meta',
      value:
        health.summary.missing_title +
        health.summary.duplicate_title +
        health.summary.missing_description,
      hint: 'Missing or duplicate titles, missing meta descriptions',
      alert:
        health.summary.missing_title +
          health.summary.duplicate_title +
          health.summary.missing_description >
        0,
      filter: 'titles',
    },
  ];
  return (
    <div className="grid grid-cols-2 gap-px overflow-hidden border-b border-[var(--color-line-soft)] bg-[var(--color-line)] sm:grid-cols-3 lg:grid-cols-6">
      {tiles.map((tile) => {
        const clickable = tile.filter != null && tile.value > 0;
        const selected = tile.filter != null && active === tile.filter;
        return clickable ? (
          <button
            key={tile.label}
            type="button"
            aria-pressed={selected}
            onClick={() => onToggle(tile.filter as IssueFilter)}
            className={`cursor-pointer bg-[var(--color-paper)] p-4 text-left hover:bg-[var(--color-line-soft)]/40 ${
              selected ? 'shadow-[inset_0_-2px_0_var(--color-accent-brand)]' : ''
            }`}
            title={selected ? 'Click to show all issues' : 'Click to filter the list'}
          >
            <TileBody tile={tile} />
          </button>
        ) : (
          <div key={tile.label} className="bg-[var(--color-paper)] p-4">
            <TileBody tile={tile} />
          </div>
        );
      })}
    </div>
  );
}

function SitemapHealthCard({ siteUuid }: { siteUuid: string }): ReactElement {
  const health = useSitemapHealth(siteUuid);
  const refresh = useRefreshSitemapHealth(siteUuid);
  const data = health.data;
  const busy = health.isLoading || refresh.isPending;

  const issueFilter = Route.useSearch().filter ?? null;
  const navigate = useNavigate();
  const setFilter = (filter: IssueFilter | null): void => {
    navigate({
      to: '/seo/sitemap',
      search: filter ? { filter } : {},
      replace: true,
    });
  };
  const toggleFilter = (filter: IssueFilter): void =>
    setFilter(issueFilter === filter ? null : filter);

  // Pixel traffic per path — turns the category list into a
  // fix-this-first list: within each severity band, the URLs real
  // visitors actually hit sort to the top.
  const topPages = useSiteTopPages(siteUuid, { days: 30, limit: 200, sort: 'pageviews' });
  const viewsByPath = useMemo(() => {
    const map = new Map<string, number>();
    for (const row of topPages.data ?? []) {
      const key = pathKey(row.url);
      map.set(key, (map.get(key) ?? 0) + row.pageviews);
    }
    return map;
  }, [topPages.data]);

  const visibleIssues = useMemo(() => {
    const rows = issueFilter
      ? (data?.issues ?? []).filter(FILTER_PREDICATES[issueFilter])
      : (data?.issues ?? []);
    return [...rows].sort(
      (a, b) =>
        severityRank(a) - severityRank(b) ||
        (viewsByPath.get(pathKey(b.url)) ?? 0) - (viewsByPath.get(pathKey(a.url)) ?? 0),
    );
  }, [data?.issues, issueFilter, viewsByPath]);

  return (
    <section className="rounded-xl border border-[var(--color-line)] bg-[var(--color-paper)]">
      <header className="flex items-center justify-between gap-2 border-b border-[var(--color-line-soft)] px-4 py-3">
        <div>
          <h3 className="text-sm font-semibold text-[var(--color-ink)]">Sitemap health</h3>
          {data && (
            <p
              className="text-xs text-[var(--color-mute)]"
              title={
                data.total_urls > data.checked
                  ? `The sitemap lists ${data.total_urls} URLs; each check covers the first ${data.checked}.`
                  : undefined
              }
            >
              {data.total_urls > data.checked
                ? `Checked ${data.checked} of ${data.total_urls} URLs`
                : `Checked ${data.checked} URLs`}
              {` · ${relativeTime(data.checked_at)}`}
            </p>
          )}
        </div>
        <Button
          variant="outline"
          size="sm"
          disabled={busy}
          onClick={() => refresh.mutate()}
          title="Re-fetch the sitemap and re-check every URL"
        >
          <ArrowsClockwise className={refresh.isPending ? 'animate-spin' : undefined} />
          Re-check
        </Button>
      </header>

      {busy && !data ? (
        <div className="space-y-2 p-4">
          <Skeleton className="h-5 w-full" />
          <Skeleton className="h-5 w-4/5" />
          <Skeleton className="h-5 w-3/5" />
        </div>
      ) : health.isError ? (
        <p className="p-4 text-sm text-[var(--color-mute)]">
          Couldn't check the sitemap just now — try re-checking in a moment.
        </p>
      ) : !data ? null : data.source_url == null ? (
        <EmptyState
          icon={TreeStructure}
          title="No sitemap found"
          description={`Neither ${data.origin}/sitemap.xml nor a Sitemap: line in robots.txt responded. Publishing one tells search engines (and us) what to index.`}
        />
      ) : (
        <>
          <DistributionBar health={data} active={issueFilter} onToggle={toggleFilter} />
          <SummaryTiles health={data} active={issueFilter} onToggle={toggleFilter} />
          {data.monitored_missing.length > 0 && (
            <p className="border-b border-[var(--color-line-soft)] px-4 py-3 text-sm text-[var(--color-mute)]">
              <Warning
                weight="fill"
                className="mr-1.5 inline align-[-0.125em] text-[var(--color-warning)]"
              />
              {data.monitored_missing.length === 1
                ? 'A page you monitor is missing from the sitemap: '
                : `${data.monitored_missing.length} pages you monitor are missing from the sitemap: `}
              <span className="text-[var(--color-ink)]">
                {data.monitored_missing.map(pathOf).join(', ')}
              </span>
            </p>
          )}
          {data.total_urls > 0 && data.lastmod_present === 0 && (
            <p className="border-b border-[var(--color-line-soft)] px-4 py-3 text-sm text-[var(--color-mute)]">
              <Warning
                weight="fill"
                className="mr-1.5 inline align-[-0.125em] text-[var(--color-warning)]"
              />
              The sitemap has no last-modified dates, so search engines can't tell which pages
              changed recently.
            </p>
          )}
          {data.issues.length === 0 ? (
            <div className="flex items-center gap-2 p-4 text-sm text-[var(--color-mute)]">
              <CheckCircle weight="fill" className="text-[var(--color-good,#16a34a)]" />
              All {data.summary.ok} checked URLs respond directly with 2xx, are indexable, and have
              clean titles, descriptions and canonicals.
            </div>
          ) : (
            <>
              {issueFilter && (
                <p className="flex items-center gap-2 border-b border-[var(--color-line-soft)] px-4 py-2 text-xs text-[var(--color-mute)]">
                  Showing {visibleIssues.length === 1 ? 'the' : visibleIssues.length}{' '}
                  {FILTER_LABELS[issueFilter]}
                  <button
                    type="button"
                    onClick={() => setFilter(null)}
                    className="cursor-pointer font-medium text-[var(--color-ink)] hover:underline"
                  >
                    Show all issues
                  </button>
                </p>
              )}
              {visibleIssues.length === 0 ? (
                <p className="p-4 text-sm text-[var(--color-mute)]">
                  No {issueFilter ? FILTER_LABELS[issueFilter] : 'issues'} right now.
                </p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>URL</TableHead>
                      <TableHead
                        className="text-right"
                        title="Pageviews in the last 30 days, from your pixel"
                      >
                        Views (30d)
                      </TableHead>
                      <TableHead className="text-right">Status</TableHead>
                      <TableHead>Issue</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {visibleIssues.slice(0, 50).map((row) => (
                      <TableRow key={row.url}>
                        <TableCell className="max-w-72 truncate font-medium" title={row.url}>
                          {displayUrl(row.url, data.origin)}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          {viewsByPath.has(pathKey(row.url)) ? (
                            formatCount(viewsByPath.get(pathKey(row.url)))
                          ) : (
                            <span className="text-[var(--color-mute)]">—</span>
                          )}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          {row.status ?? '—'}
                        </TableCell>
                        <TableCell
                          className="max-w-80 truncate text-[var(--color-mute)]"
                          title={issueLabel(row, data.origin)}
                        >
                          {issueLabel(row, data.origin)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
              {visibleIssues.length > 50 && (
                <p className="border-t border-[var(--color-line-soft)] px-4 py-2 text-xs text-[var(--color-mute)]">
                  Showing the 50 worst of {visibleIssues.length} — use the tiles above to narrow the
                  list.
                </p>
              )}
            </>
          )}
        </>
      )}
    </section>
  );
}
